import React from 'react';
import { Divider, FlatContentCard, Heading, LinkTarget, Text } from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';

import { TopicAssociatedContent } from 'src/graphql/queries/GetTopicCollection';
import { sendUIEvent } from 'src/utils/analytics';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { useNavigateByHost } from 'src/hooks/useNavigateByHost';
import { ImageAttrs } from 'src/utils/contentful';
import { getContentLink, getContentDescription, getContentLinkTarget } from 'src/pages/utils/utils';
import { ContentItem } from 'src/types/contentful/base-content';
import { VideoBlock } from 'src/pages/LessonPage/children/blocks/VideoBlock';
import { OrVideo } from 'src/graphql/generated/graphql';

export interface ContentBlockProps {
  title: string;
  subtitle: string;
  associatedContent: TopicAssociatedContent[];
  imageAttrs?: ImageAttrs;
  videoAttrs?: OrVideo;
}

export const ContentBlock: React.FC<ContentBlockProps> = ({
  title,
  subtitle,
  associatedContent,
  imageAttrs,
  videoAttrs,
}) => {
  const { user } = useAtlassianUser();
  const navigateByHost = useNavigateByHost();

  const handleLearnMoreClick = (cardTitle: string, link?: string, target?: LinkTarget): void => {
    sendUIEvent(
      'relatedTopicCollectionScreen',
      'link',
      'clicked',
      'cardLink',
      {
        contentBlockTitle: title,
        cardTitle: cardTitle,
      },
      user?.account_id,
    );

    if (link) {
      navigateByHost(link, target);
    }
  };

  return (
    <div css={containerStyles}>
      {imageAttrs && (
        <div css={contentBlockImageContainerStyles}>
          <img css={contentBlockImageStyles} src={imageAttrs.src} alt={imageAttrs.alt} />
        </div>
      )}
      {title && (
        <div css={headerStyles} data-testid="content-block-title">
          <Heading level={2}>{title}</Heading>
        </div>
      )}
      {subtitle && (
        <div css={subtitleStyles} data-testid="content-block-subtitle">
          <Text size="large">{subtitle}</Text>
        </div>
      )}
      {videoAttrs && (
        <div
          css={[videoContainerStyles, !associatedContent.length && css({ marginBottom: 0 })]}
          data-testid="content-block-video"
        >
          <VideoBlock entry={videoAttrs} fields={videoAttrs} />
        </div>
      )}
      <div css={associatedContentBlockContainerStyles}>
        {associatedContent?.length > 0 &&
          associatedContent.map((content) => {
            const contentItem = content as ContentItem;
            const target = getContentLinkTarget(contentItem);

            return (
              <div
                css={associatedContentBlockStyles}
                key={`${content?.sys.id}`}
                data-testid="content-block-associated-card"
              >
                <Divider color={token('color.border')} height={1} />
                <FlatContentCard
                  title={content.title}
                  link={getContentLink(contentItem)}
                  text={getContentDescription(contentItem)}
                  onClick={(title: string) => handleLearnMoreClick(title, getContentLink(contentItem), target)}
                  target={target}
                />
              </div>
            );
          })}
      </div>
    </div>
  );
};

const containerStyles = css({
  maxWidth: '1440px',
  width: '100%',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',

  [media.below.lg]: {
    padding: `0 ${token('space.300')}`,
  },

  [media.below.sm]: {
    padding: `0 ${token('space.200')} !important`,
  },
});

const contentBlockImageContainerStyles = css({
  marginBottom: token('space.400'),

  [media.below.xs]: {
    marginBottom: token('space.300'),
  },
});

const contentBlockImageStyles = css({
  height: '80px',
  width: 'auto',
  maxWidth: '100%',
  boxSizing: 'border-box',
});

const headerStyles = css({
  maxWidth: '720px',
  width: '100%',
  textAlign: 'center',
  marginBottom: token('space.200'),
});

const subtitleStyles = css({
  maxWidth: '720px',
  width: '100%',
  marginBottom: token('space.600'),
  textAlign: 'center',

  [media.below.xs]: {
    marginBottom: token('space.300'),
  },
});

const videoContainerStyles = css({
  width: '814px',
  height: 'auto',
  overflow: 'hidden',
  marginBottom: `${token('space.600')}`,

  [media.below.md]: {
    maxWidth: '100%',
    height: 'auto',
    marginBottom: `${token('space.400')}`,
  },
});

const associatedContentBlockContainerStyles = css({
  width: '100%',
  maxWidth: '1022px',
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  columnGap: '56px',

  [media.below.md]: {
    gridTemplateColumns: '1fr',
  },
});

const associatedContentBlockStyles = css({
  maxWidth: '482px',
  width: '100%',
  margin: '0 auto',

  '&:nth-last-child(-n + 2)': {
    borderBottom: `1px solid ${token('color.border')}`,
  },

  [media.below.md]: {
    maxWidth: 'unset',

    '&:nth-last-child(2)': {
      borderBottom: 'none',
    },
  },
});
